"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  MessageSquare,
  X,
  Send,
  Sparkles,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  BrainCircuit,
  UserCheck,
} from "lucide-react";
import { Badge } from "@/components/ui/feedback";

interface Message {
  id: string;
  sender: "user" | "bot";
  text: string;
  timestamp: string;
}

const QUICK_ACTIONS = [
  {
    label: "Stato Progetti Watch",
    query: "Qual è lo stato dei progetti in Watch o Risk?",
    icon: <AlertTriangle className="h-3 w-3 text-warning" />,
  },
  {
    label: "Riepilogo Cassa & Runway",
    query: "Dammi un riepilogo della situazione finanziaria e runway.",
    icon: <TrendingUp className="h-3 w-3 text-success" />,
  },
  {
    label: "Analisi Burnout Team",
    query: "Ci sono allarmi per il carico di lavoro del team (burnout)?",
    icon: <UserCheck className="h-3 w-3 text-error" />,
  },
  {
    label: "Spiegami la War Room",
    query: "Come funziona la Voice War Room?",
    icon: <BrainCircuit className="h-3 w-3 text-primary-500" />,
  },
];

export function CompanionBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      sender: "bot",
      text: "Ciao! Sono il tuo Assistente AI Cleos. Sono collegato al Grafo della tua organizzazione e posso fornirti aggiornamenti in tempo reale su cassa, progetti in watch, burnout del team o aiutarti a redigere documenti.",
      timestamp: new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isOpen, messages, isTyping]);

  const handleSendMessage = (text: string) => {
    if (!text.trim()) return;

    // Add user message
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      sender: "user",
      text,
      timestamp: new Date().toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputValue("");
    setIsTyping(true);

    // Simulate Bot response based on user query
    setTimeout(() => {
      let replyText = "";
      const lowerText = text.toLowerCase();

      if (
        lowerText.includes("watch") ||
        lowerText.includes("risk") ||
        lowerText.includes("progett")
      ) {
        replyText =
          "Attualmente il Grafo rileva 2 progetti critici in stato di 'Risk':\n1. **Cleos Voice Agent** (PM: Giulia Bianchi) - Latenza nella pipeline RAG e designer in sovraccarico.\n2. **ERP Bridge** (PM: Alessandro Neri) - Feedback del cliente in ritardo.\nCi sono inoltre 4 progetti in stato di 'Watch' monitorati dal sistema.";
      } else if (
        lowerText.includes("cassa") ||
        lowerText.includes("runway") ||
        lowerText.includes("finanz")
      ) {
        replyText =
          "Situazione Finanziaria Corrente:\n- **Liquidità**: €184.300 in cassa.\n- **Burn Rate**: stimato in €28.000/mese.\n- **Runway**: 6.5 mesi di autonomia.\n- **Inflow atteso**: fattura da riscuotere da Helix Srl per €42.000 con scadenza tra 11 giorni.";
      } else if (
        lowerText.includes("burnout") ||
        lowerText.includes("team") ||
        lowerText.includes("carico")
      ) {
        replyText =
          "Analisi Carico di Lavoro & Benessere:\n- Rilevato **alert di burnout** per Giulia Bianchi (Lead Designer) per via dell'elevato numero di task critici assegnati su Cleos Voice Agent.\n- Il resto del team si trova in zona di capacità ottimale (saturazione media al 78%). Ti consiglio di ridistribuire 2 task di design.";
      } else if (
        lowerText.includes("war room") ||
        lowerText.includes("riunione")
      ) {
        replyText =
          "La **Voice War Room** è una consolle audio interattiva (disponibile in Strategy > War Room). Durante i tuoi meeting, registra l'audio: l'AI trascriverà la conversazione in tempo reale e proporrà l'inserimento immediato di Task, Rischi o Decisioni nel database con un semplice clic.";
      } else {
        replyText =
          "Sono qui per guidarti nella gestione della tua azienda. Puoi chiedermi dello stato di cassa, dei progetti critici o di effettuare analisi sul burnout del team. Vuoi fare una prova con le azioni rapide in basso?";
      }

      const botMsg: Message = {
        id: `bot-${Date.now()}`,
        sender: "bot",
        text: replyText,
        timestamp: new Date().toLocaleTimeString("it-IT", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setMessages((prev) => [...prev, botMsg]);
      setIsTyping(false);
    }, 1200);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSendMessage(inputValue);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* Expanded Chat Box */}
      {isOpen && (
        <Card className="mb-4 w-90 h-[500px] border border-border bg-card shadow-hover flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-5 duration-200">
          {/* Header */}
          <CardHeader className="p-4 border-b border-border/50 bg-card flex flex-row items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary-500/10 border border-primary-500/20 flex items-center justify-center text-primary-600">
                <Sparkles className="h-4.5 w-4.5 fill-primary-500/10" />
              </div>
              <div>
                <CardTitle className="text-xs font-bold text-text-primary leading-tight flex items-center gap-1.5">
                  Cleos Copilota
                </CardTitle>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                  <span className="text-[10px] text-text-muted">
                    Online • Scansione Grafo
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-card-muted rounded-md text-text-secondary hover:text-text-primary transition-colors cursor-pointer"
            >
              <X className="h-4 w-4" />
            </button>
          </CardHeader>

          {/* Messages Body */}
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin bg-card-muted/5">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col max-w-[85%] ${
                  msg.sender === "user"
                    ? "ml-auto items-end"
                    : "mr-auto items-start"
                }`}
              >
                <div
                  className={`p-3 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                    msg.sender === "user"
                      ? "bg-primary-600 text-text-on-primary font-medium"
                      : "bg-card border border-border text-text-primary shadow-xs"
                  }`}
                >
                  {msg.text}
                </div>
                <span className="text-[9px] text-text-muted mt-1 px-1">
                  {msg.timestamp}
                </span>
              </div>
            ))}

            {isTyping && (
              <div className="flex flex-col max-w-[85%] mr-auto items-start">
                <div className="p-3 rounded-xl bg-card border border-border text-xs flex items-center gap-2 text-text-muted shadow-xs">
                  <RefreshCw className="h-3 w-3 animate-spin text-primary-500" />
                  <span>Sto elaborando i dati aziendali...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Quick Actions Chips */}
          <div className="p-3 border-t border-border/40 bg-card shrink-0 space-y-2">
            <span className="text-[9px] font-bold text-text-muted uppercase tracking-wider block">
              Azioni Rapide
            </span>
            <div className="grid grid-cols-2 gap-1.5">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.label}
                  onClick={() => handleSendMessage(action.query)}
                  disabled={isTyping}
                  className="p-1.5 text-[10px] rounded-lg border border-border bg-card hover:bg-card-muted text-left text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1.5 truncate cursor-pointer disabled:opacity-50"
                >
                  {action.icon}
                  <span className="truncate font-semibold">{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Input Footer */}
          <CardFooter className="p-3 border-t border-border/50 bg-card flex gap-2 shrink-0">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Chiedimi della cassa, progetti in watch..."
              disabled={isTyping}
              className="flex-1 h-9 rounded-lg border border-border bg-card px-3 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 text-foreground"
            />
            <Button
              size="icon"
              onClick={() => handleSendMessage(inputValue)}
              disabled={!inputValue.trim() || isTyping}
              className="h-9 w-9 shrink-0"
            >
              <Send className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Floating Button Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`rounded-full h-12 w-12 text-white shadow-hover flex items-center justify-center cursor-pointer transition-transform hover:scale-105 active:scale-95 ${
          isOpen
            ? "bg-card border border-border text-foreground hover:bg-card-muted"
            : "bg-primary-600 hover:bg-primary-700"
        }`}
      >
        {isOpen ? (
          <X className="h-5 w-5 text-text-primary" />
        ) : (
          <MessageSquare className="h-5 w-5 text-text-on-primary fill-transparent" />
        )}
      </button>
    </div>
  );
}
